import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import type { startseite } from "@progressive-development/politik-model";

import { zahl, zeitpunktGenau } from "../../../common/format/de-format.js";
import { blockStyles } from "../../../common/styles/block-styles.js";

/**
 * Der Datenstand als Tabelle — eine Zeile je Quelle, der jüngste Lauf darunter
 * (FACHKONZEPT-rahmen.md, 4.1). Gestaltet wie ein Laufzettel, in Mono.
 *
 * Heute zeigt die Zeile, was `verzeichnisse/datenstand` hergibt: Kennung der
 * Quelle, letzter Lauf, Status, abgerufene Objekte. Die Zeile je DATENSATZ mit
 * Anbieter, Lizenz und Umfang kommt mit `verzeichnisse/quellen`
 * (ANFORDERUNG-quellen-verzeichnis.md) — dann wächst diese Tabelle, die Seite
 * bleibt.
 *
 * Der Stand ist der jüngste Lauf, nicht ein eigener Zeitstempel: Ein
 * „erzeugt am" der Anzeige sagt nur, wann jemand die Seite geöffnet hat.
 */
@customElement("app-datenstand-tabelle")
@localized()
export class AppDatenstandTabelle extends LitElement {
  /** Ein Lauf je Quelle — der jüngste abgeschlossene. */
  @property({ attribute: false }) datenstand: startseite.DatenstandEintrag[] = [];

  static override styles = [
    blockStyles,
    css`
      :host {
        padding: 0;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        font-variant-numeric: tabular-nums;
      }
      caption {
        text-align: left;
        padding-bottom: var(--portal-space-2);
        color: var(--portal-ink-2);
        text-transform: uppercase;
        letter-spacing: var(--portal-tracking-caps);
      }
      th,
      td {
        text-align: left;
        padding: var(--portal-space-3) var(--portal-space-3) var(--portal-space-3) 0;
        border-top: 1px solid var(--portal-linie);
        font-weight: 400;
        vertical-align: baseline;
      }
      th {
        color: var(--portal-ink);
      }
      td {
        color: var(--portal-ink-2);
      }
      tr:last-child td,
      tr:last-child th {
        border-bottom: 1px solid var(--portal-linie);
      }
      .zahl {
        text-align: right;
      }
      .erzeugt {
        margin: var(--portal-space-4) 0 0;
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
      }
      @media (max-width: 640px) {
        .zahl {
          display: none;
        }
      }
    `,
  ];

  override render() {
    if (this.datenstand.length === 0) return nothing;
    const juengster = juengsterLauf(this.datenstand);
    return html`
      <table>
        <caption>
          ${msg("Letzter Lauf je Quelle", { id: "app.start.quellen.caption" })}
        </caption>
        <thead>
          <tr>
            <th scope="col">${msg("Quelle", { id: "app.start.quellen.quelle" })}</th>
            <th scope="col">${msg("Letzter Lauf", { id: "app.start.quellen.lauf" })}</th>
            <th scope="col">${msg("Status", { id: "app.start.quellen.status" })}</th>
            <th scope="col" class="zahl">${msg("Objekte", { id: "app.start.quellen.objekte" })}</th>
          </tr>
        </thead>
        <tbody>
          ${this.datenstand.map(
            (d) => html`
              <tr>
                <th scope="row">${d.id}</th>
                <td>${zeitpunktGenau(d.letzterLauf)}</td>
                <td>${d.status}</td>
                <td class="zahl">${zahl(d.abgerufen)}</td>
              </tr>
            `,
          )}
        </tbody>
      </table>
      ${
        juengster
          ? html`<p class="erzeugt">
              ${msg("Jüngster Lauf", { id: "app.start.quellen.juengster" })}
              ${zeitpunktGenau(juengster)}
            </p>`
          : nothing
      }
    `;
  }
}

/** Der jüngste Lauf über alle Quellen; ohne Daten gibt es keinen. */
export function juengsterLauf(
  datenstand: readonly startseite.DatenstandEintrag[],
): string | undefined {
  return datenstand
    .map((d) => d.letzterLauf)
    .filter(Boolean)
    .sort()
    .at(-1);
}

declare global {
  interface HTMLElementTagNameMap {
    "app-datenstand-tabelle": AppDatenstandTabelle;
  }
}
